import { Observable, Subject } from 'rxjs';

import { ClientCommand, ServerEvent } from './agent-ws.types';

/** Состояние WebSocket-соединения. */
export type WsConnectionState = 'connecting' | 'connected' | 'closed' | 'error';

/**
 * Тонкая обёртка над нативным WebSocket для чата с AI-агентом.
 * Парсит входящие JSON-события в типизированный `ServerEvent`.
 * При неожиданном закрытии или коде 4001 эмитит `{ kind: 'error' }` в `events$`.
 * Reconnect не выполняет — пробрасывает ошибку наверх.
 */
export class AgentWsConnection {
    /** Входящие события от сервера. */
    public readonly events$: Observable<ServerEvent>;

    private readonly eventsSubject = new Subject<ServerEvent>();
    private readonly ws: WebSocket;
    private closed = false;

    public constructor(url: string) {
        this.events$ = this.eventsSubject.asObservable();
        this.ws = new WebSocket(url);
        this.ws.onmessage = (event) => this.handleMessage(event);
        this.ws.onclose = (event) => this.handleClose(event);
        this.ws.onerror = () => this.handleError();
    }

    /**
     * Отправить команду на сервер.
     * Игнорирует вызов, если соединение уже закрыто.
     */
    public send(cmd: ClientCommand): void {
        if (this.closed || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }

        this.ws.send(JSON.stringify(cmd));
    }

    /**
     * Отправить бинарный фрейм (аудиочанк) на сервер.
     * Игнорирует вызов, если соединение уже закрыто.
     */
    public sendBinary(data: ArrayBuffer): void {
        if (this.closed || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }

        this.ws.send(data);
    }

    /** Закрыть соединение. Повторные вызовы безопасны. */
    public close(): void {
        if (this.closed) {
            return;
        }

        this.closed = true;
        this.ws.close();
        this.eventsSubject.complete();
    }

    private handleMessage(event: MessageEvent): void {
        let parsed: ServerEvent;

        try {
            parsed = JSON.parse(event.data as string) as ServerEvent;
        } catch {
            return;
        }

        this.eventsSubject.next(parsed);
    }

    private handleClose(event: CloseEvent): void {
        if (this.closed) {
            return;
        }

        this.closed = true;

        if (event.code === 4001) {
            this.eventsSubject.next({ kind: 'error', error: 'unauthorized', code: '4001' });
        } else {
            this.eventsSubject.next({ kind: 'error', error: 'connection_closed' });
        }

        this.eventsSubject.complete();
    }

    private handleError(): void {
        if (this.closed) {
            return;
        }

        this.eventsSubject.next({ kind: 'error', error: 'websocket_error' });
    }
}
